import { Injectable } from '@angular/core';
import type { Worker, RecognizeResult } from 'tesseract.js';
import { Subject, BehaviorSubject } from 'rxjs';
import { ocrIdentifierValuesMatch } from '../utils/ocr-identifier-compare';

/** Min confidence to treat a PSM result as usable when early-exiting on expected match. */
export const TESSERACT_MULTI_PSM_USABLE_CONFIDENCE_FLOOR = 30;

type Rect = { x1: number; y1: number; x2: number; y2: number };
type Zone = { pageNumber: number; rect: Rect };
type Read = { text: string; confidence: number };
/** Full page rendered once at SCALE for cheap zone extracts. */
type PageBitmap = { canvas: OffscreenCanvas; width: number; height: number; scale: number };
type OcrImage = Blob | OffscreenCanvas;

/** Shared crop handle for detection lanes (page bitmap + rect cache). */
export type DetectionCropSession = {
    renderCrop: (zone: Zone) => Promise<OffscreenCanvas | null>;
    destroy: () => void;
};

/**
 * App OCR engine: PDF crops + Tesseract.
 * Prefetch/warm only from upload / document-types hosts — not sign-link / dashboard.
 */
@Injectable({ providedIn: 'root' })
export class TesseractEngineService {
    private static readonly DPI = '300';
    private static readonly SCALE = 2;
    private static readonly POOL_MAX = 4;
    private static readonly POOL_WARM = 3;
    private static readonly PSM = ['7', '6'] as const;

    private worker: Worker | null = null;
    private ready = false;
    private initP: Promise<void> | null = null;
    private ocrQ: Promise<void> = Promise.resolve();
    private pdfQ: Promise<void> = Promise.resolve();
    private pdfWorker: any = null;
    private pool: Worker[] = [];
    private poolReady = false;
    private poolP: Promise<void> | null = null;
    private free: Worker[] = [];
    private waiters: ((w: Worker) => void)[] = [];
    private debugUrlSubject = new BehaviorSubject<string | null>(null);
    readonly debugUrl$ = this.debugUrlSubject.asObservable();
    private resultSubject = new Subject<string>();
    readonly ocrResult$ = this.resultSubject.asObservable();
    private modP: Promise<typeof import('tesseract.js')> | null = null;

    /** Fire-and-forget: start loading the main Tesseract worker. */
    prefetch(): void { void this.ensureReady(); }

    /** Fire-and-forget: warm default detection pool (3 workers). */
    warmDetectionPool(): void { void this.ensurePool(TesseractEngineService.POOL_WARM); }

    /** Await until at least `n` pool workers exist (grows up to POOL_MAX). */
    async awaitDetectionPool(n = TesseractEngineService.POOL_WARM): Promise<void> {
        await this.ensurePool(Math.min(n, TesseractEngineService.POOL_MAX));
    }

    get detectionWorkerCount(): number { return this.pool.length; }

    /** Tear down pool, shared PDF worker, and main OCR worker. */
    async destroy(): Promise<void> {
        for (const w of this.pool) { try { await w.terminate(); } catch { /* */ } }
        this.pool = []; this.free = []; this.waiters = []; this.poolReady = false;
        if (this.pdfWorker) { try { this.pdfWorker.destroy(); } catch { /* */ } this.pdfWorker = null; }
        if (this.worker) { await this.worker.terminate(); this.worker = null; this.ready = false; }
    }

    /** OCR one PDF box (text only) — selection / identifier tools. */
    async processSelection(pageNumber: number, rect: Rect): Promise<string> {
        return (await this.readZone(pageNumber, rect, 'processSelection'))?.text ?? '';
    }

    /** OCR one PDF box with confidence — selection tools that need quality. */
    async processSelectionWithConfidence(pageNumber: number, rect: Rect): Promise<Read> {
        return (await this.readZone(pageNumber, rect, 'processSelectionWithConfidence')) ?? { text: '', confidence: 0 };
    }

    /** OCR many zones in order on the main worker (progress callback optional). */
    async processBatch(zones: Zone[], onProgress?: (done: number, total: number) => void): Promise<string[]> {
        await this.ensureReady();
        if (!this.ready || !this.worker || !zones.length) return zones.map(() => '');
        const blobs = await this.cropMany(zones);
        const out: string[] = [];
        for (let i = 0; i < blobs.length; i++) {
            try {
                if (!blobs[i]) out.push('');
                else {
                    const best = await this.q(this, 'ocrQ', () => this.recognize(blobs[i]!, { pipeline: 'processBatch' }));
                    this.resultSubject.next(best.text);
                    out.push(best.text);
                }
            } catch { out.push(''); }
            onProgress?.(i + 1, blobs.length);
        }
        return out;
    }

    /** OCR on a fixed pool worker by index (detection lanes). Falls back if index missing. */
    async recognizeOnWorker(workerIndex: number, image: OcrImage, trace: Record<string, unknown>): Promise<string> {
        const w = this.pool[workerIndex];
        if (!w) return this.recognizeFallback(image, trace);
        const best = await this.recognize(image, trace, w);
        this.resultSubject.next(best.text);
        return best.text;
    }

    /**
     * Open a detection crop session: private PDF copy + dedicated PDF.js worker.
     * Renders each page once to a bitmap; zone crops are drawImage extracts (cloned per call).
     */
    async openDetectionSession(): Promise<DetectionCropSession> {
        await this.ensureReady();
        const bytes = await this.readViewerPdfBytes();
        const opened = bytes ? await this.openPdfDocument(bytes, true) : null;
        if (!opened) return { renderCrop: async () => null, destroy: () => {} };

        const { pdf, dedicatedWorker } = opened;
        const pages = new Map<number, Promise<PageBitmap | null>>();
        const crops = new Map<string, OffscreenCanvas>();
        const self = this;
        const r = (n: number) => Math.round(n * 100) / 100;

        return {
            async renderCrop(zone) {
                const key = `${zone.pageNumber}:${r(zone.rect.x1)}:${r(zone.rect.y1)}:${r(zone.rect.x2)}:${r(zone.rect.y2)}`;
                const hit = crops.get(key);
                if (hit) return self.cloneCanvas(hit);
                try {
                    let p = pages.get(zone.pageNumber);
                    if (!p) { p = self.renderPageBitmap(pdf, zone.pageNumber); pages.set(zone.pageNumber, p); }
                    const bitmap = await p;
                    if (!bitmap) return null;
                    const crop = self.cropFromPageBitmap(bitmap, zone.rect);
                    if (crop) crops.set(key, crop);
                    return crop ? self.cloneCanvas(crop) : null;
                } catch { return null; }
            },
            destroy() {
                pages.clear(); crops.clear();
                try { pdf.destroy?.(); } catch { /* */ }
                try { dedicatedWorker?.destroy?.(); } catch { /* */ }
            },
        };
    }

    /** Clone so parallel OCR lanes never share one canvas buffer. */
    private cloneCanvas(src: OffscreenCanvas): OffscreenCanvas {
        const out = new OffscreenCanvas(src.width, src.height) as any;
        (out.getContext('2d') as OffscreenCanvasRenderingContext2D).drawImage(src as any, 0, 0);
        return out;
    }

    /** Expensive once per page: full-page render at SCALE into OffscreenCanvas. */
    private async renderPageBitmap(pdf: { getPage: (n: number) => Promise<any> }, pageNumber: number): Promise<PageBitmap | null> {
        try {
            const scale = TesseractEngineService.SCALE;
            const page = await pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale });
            const width = Math.max(1, Math.round(viewport.width));
            const height = Math.max(1, Math.round(viewport.height));
            const canvas = new OffscreenCanvas(width, height) as any;
            const ctx = canvas.getContext('2d')! as OffscreenCanvasRenderingContext2D;
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, width, height);
            await page.render({ canvasContext: ctx as any, viewport, intent: 'print' }).promise;
            return { canvas, width, height, scale };
        } catch { return null; }
    }

    /** Cheap zone extract from a cached page bitmap (no PNG encode). */
    private cropFromPageBitmap(bitmap: PageBitmap, rect: Rect): OffscreenCanvas | null {
        const s = bitmap.scale;
        const x = Math.min(rect.x1, rect.x2) * s, y = Math.min(rect.y1, rect.y2) * s;
        const w = Math.abs(rect.x2 - rect.x1) * s, h = Math.abs(rect.y2 - rect.y1) * s;
        if (w < 1 || h < 1) return null;
        const sx = Math.max(0, Math.floor(x)), sy = Math.max(0, Math.floor(y));
        const sw = Math.max(1, Math.min(Math.ceil(w), bitmap.width - sx));
        const sh = Math.max(1, Math.min(Math.ceil(h), bitmap.height - sy));
        if (sw < 1 || sh < 1 || sx >= bitmap.width || sy >= bitmap.height) return null;
        const out = new OffscreenCanvas(sw, sh) as any;
        const ctx = out.getContext('2d')! as OffscreenCanvasRenderingContext2D;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, sw, sh);
        ctx.drawImage(bitmap.canvas as any, sx, sy, sw, sh, 0, 0, sw, sh);
        return out;
    }

    /** Paths to quiet Tesseract assets under /assets/tesseract. */
    private opts() {
        const o = window.location.origin;
        return {
            logger: () => {}, errorHandler: () => {},
            workerPath: `${o}/assets/tesseract/worker-quiet.js`,
            corePath: `${o}/assets/tesseract/`, langPath: `${o}/assets/tesseract/`, gzip: false as const,
        };
    }

    /** Lazy dynamic import of tesseract.js. */
    private mod(): Promise<typeof import('tesseract.js')> { return this.modP ??= import('tesseract.js'); }

    /** Create/reuse the main ara+eng worker (selection / batch path). */
    private ensureReady(): Promise<void> {
        if (this.ready && this.worker) return Promise.resolve();
        if (this.initP) return this.initP;
        this.initP = (async () => {
            try {
                const { createWorker, setLogging } = await this.mod();
                setLogging(false);
                this.worker = await createWorker('ara+eng', 1, this.opts());
                await this.worker.setParameters({ user_defined_dpi: TesseractEngineService.DPI as any });
                this.ready = true;
            } catch { this.worker = null; this.ready = false; }
        })().finally(() => { this.initP = null; });
        return this.initP;
    }

    /**
     * Ensure detection pool has at least `targetSize` workers (max POOL_MAX).
     * Grows from warm size (3) to 4 only when detection asks for a 4th lane.
     */
    private ensurePool(targetSize = TesseractEngineService.POOL_WARM): Promise<void> {
        const target = Math.min(Math.max(1, targetSize), TesseractEngineService.POOL_MAX);
        if (this.pool.length >= target) { this.poolReady = this.pool.length > 0; return Promise.resolve(); }
        if (this.poolP) return this.poolP.then(() => this.ensurePool(target));
        this.poolP = (async () => {
            const { createWorker } = await this.mod();
            while (this.pool.length < target) {
                try {
                    const w = await createWorker('ara+eng', 1, this.opts());
                    await w.setParameters({ user_defined_dpi: TesseractEngineService.DPI as any });
                    this.pool.push(w); this.free.push(w);
                } catch { break; }
            }
            this.poolReady = this.pool.length > 0;
        })().finally(() => { this.poolP = null; });
        return this.poolP;
    }

    /** Serialize tasks on ocrQ or pdfQ so work does not overlap on shared resources. */
    private q<T>(self: this, key: 'ocrQ' | 'pdfQ', task: () => Promise<T>): Promise<T> {
        const run = self[key].then(task, task);
        self[key] = run.then(() => undefined, () => undefined);
        return run;
    }

    /** Crop one zone then OCR on the main worker (queued). */
    private async readZone(pageNumber: number, rect: Rect, pipeline: string): Promise<Read | null> {
        await this.ensureReady();
        if (!this.ready || !this.worker) return null;
        try {
            const blob = await this.q(this, 'pdfQ', () => this.cropOne(pageNumber, rect));
            if (!blob) return null;
            const best = await this.q(this, 'ocrQ', () => this.recognize(blob, { pipeline, pageNumber, rect: { ...rect } }));
            this.resultSubject.next(best.text);
            return best;
        } catch { return null; }
    }

    /** Borrow a free pool worker, else fall back to the main worker queue. */
    private async recognizeFallback(image: OcrImage | null, trace: Record<string, unknown>): Promise<string> {
        if (!image) return '';
        try {
            await this.ensurePool();
            if (this.poolReady) {
                const w = this.free.pop() ?? await new Promise<Worker>((r) => this.waiters.push(r));
                try {
                    const best = await this.recognize(image, trace, w);
                    this.resultSubject.next(best.text);
                    return best.text;
                } finally {
                    const next = this.waiters.shift();
                    if (next) next(w); else this.free.push(w);
                }
            }
        } catch { /* main */ }
        await this.ensureReady();
        if (!this.ready || !this.worker) return '';
        try {
            const best = await this.q(this, 'ocrQ', () => this.recognize(image, trace));
            this.resultSubject.next(best.text);
            return best.text;
        } catch { return ''; }
    }

    /**
     * Run PSM 7 then 6; keep the better read.
     * For doc-type detection, stop early if expected value already matches with usable confidence.
     */
    private async recognize(image: OcrImage, trace: Record<string, unknown> = {}, target?: Worker): Promise<Read> {
        const w = target ?? this.worker!;
        const expected = trace['pipeline'] === 'docTypeDetection' ? trace['expectedValue'] as string | undefined : undefined;
        let best: Read = { text: '', confidence: 0 };
        for (const psm of TesseractEngineService.PSM) {
            await w.setParameters({ tessedit_pageseg_mode: psm as any, user_defined_dpi: TesseractEngineService.DPI as any });
            try {
                const result: RecognizeResult = await w.recognize(image as any, undefined, { debug: true });
                const text = (result.data.text ?? '').split(/\n+/).map((l) => l.trim()).filter(Boolean).join('\n').trim();
                const confidence = result.data.confidence ?? 0;
                if (this.better({ text, confidence }, best)) best = { text, confidence };
                if (expected && best.text && best.confidence >= TESSERACT_MULTI_PSM_USABLE_CONFIDENCE_FLOOR
                    && ocrIdentifierValuesMatch(best.text, expected)) return best;
            } catch { /* next */ }
        }
        return best.text.trim().length <= 2 && best.confidence < 72 ? { text: '', confidence: 0 } : best;
    }

    /** Prefer usable-confidence text; among equals prefer longer then higher confidence. */
    private better(n: Read, c: Read): boolean {
        if (!n.text) return false;
        if (!c.text) return true;
        const f = TESSERACT_MULTI_PSM_USABLE_CONFIDENCE_FLOOR;
        const nOk = n.confidence >= f, cOk = c.confidence >= f;
        if (nOk && cOk) return n.text.length !== c.text.length ? n.text.length > c.text.length : n.confidence > c.confidence;
        if (nOk !== cOk) return nOk;
        return n.confidence !== c.confidence ? n.confidence > c.confidence : n.text.length > c.text.length;
    }

    /** Crop all zones via a temp PDF copy; fall back to per-zone cropOne if that fails. */
    private async cropMany(zones: Zone[]): Promise<(Blob | null)[]> {
        try {
            const pdf = await this.openPdfCopy();
            if (pdf) {
                try {
                    const blobs = await this.cropOnPdf(pdf, zones);
                    const last = [...blobs].reverse().find(Boolean);
                    if (last) this.debug(last);
                    return blobs;
                } finally { try { pdf.destroy?.(); } catch { /* */ } }
            }
        } catch { /* fallback */ }
        const out: (Blob | null)[] = [];
        for (const z of zones) {
            try { out.push(await this.q(this, 'pdfQ', () => this.cropOne(z.pageNumber, z.rect))); }
            catch { out.push(null); }
        }
        return out;
    }

    /** Group zones by page, getPage once per page, render each rect to a PNG blob. */
    private async cropOnPdf(
        pdf: { getPage: (n: number) => Promise<any> }, zones: Zone[], cache?: Map<number, any>,
    ): Promise<(Blob | null)[]> {
        const byPage = new Map<number, { i: number; z: Zone }[]>();
        zones.forEach((z, i) => { const g = byPage.get(z.pageNumber) ?? []; g.push({ i, z }); byPage.set(z.pageNumber, g); });
        const results: (Blob | null)[] = new Array(zones.length).fill(null);
        const s = TesseractEngineService.SCALE;
        await Promise.all([...byPage.entries()].map(async ([pageNumber, group]) => {
            let page = cache?.get(pageNumber);
            if (!page) {
                try { page = await pdf.getPage(pageNumber); cache?.set(pageNumber, page); } catch { return; }
            }
            for (const { i, z } of group) {
                try {
                    const w = Math.abs(z.rect.x2 - z.rect.x1), h = Math.abs(z.rect.y2 - z.rect.y1);
                    if (w < 1 || h < 1) continue;
                    results[i] = await this.render(page, z.rect, Math.max(1, Math.round(w * s)), Math.max(1, Math.round(h * s)), s);
                } catch { /* */ }
            }
        }));
        return results;
    }

    /**
     * Crop one rect: prefer a short-lived PDF copy, else viewer pdfDocument after idle.
     * Viewer path retries if PDF.js is mid-render/cleanup.
     */
    private async cropOne(pageNumber: number, rect: Rect): Promise<Blob | null> {
        const app = (window as any).PDFViewerApplication;
        if (!app?.pdfDocument) return null;
        const w = Math.abs(rect.x2 - rect.x1), h = Math.abs(rect.y2 - rect.y1);
        if (w < 1 || h < 1) return null;
        const s = TesseractEngineService.SCALE;
        const cw = Math.max(1, Math.round(w * s)), ch = Math.max(1, Math.round(h * s));
        try {
            const pdf = await this.openPdfCopy();
            if (pdf) {
                try {
                    const blob = await this.render(await pdf.getPage(pageNumber), rect, cw, ch, s);
                    if (blob) { this.debug(blob); return blob; }
                } finally { try { pdf.destroy?.(); } catch { /* */ } }
            }
        } catch { /* viewer */ }
        const fromViewer = async () => {
            const blob = await this.render(await app.pdfDocument.getPage(pageNumber), rect, cw, ch, s);
            if (blob) this.debug(blob);
            return blob;
        };
        await this.waitIdle();
        try { return await fromViewer(); }
        catch (err) {
            if (!/currently rendering|startCleanup/i.test(String((err as any)?.message ?? err))) throw err;
            await this.waitIdle(120);
            return fromViewer();
        }
    }

    /** Copy of the open viewer's PDF bytes (for getDocument without touching the UI doc). */
    private async readViewerPdfBytes(): Promise<Uint8Array | null> {
        const app = (window as any).PDFViewerApplication;
        if (!app?.pdfDocument?.getData) return null;
        try {
            const raw = await app.pdfDocument.getData();
            return raw instanceof Uint8Array ? new Uint8Array(raw) : new Uint8Array(raw as ArrayBuffer);
        } catch { return null; }
    }

    /** Short-lived PDF.js document on the shared PDF worker (selection/batch crops). */
    private async openPdfCopy(): Promise<any | null> {
        const bytes = await this.readViewerPdfBytes();
        return bytes ? (await this.openPdfDocument(bytes, false))?.pdf ?? null : null;
    }

    /**
     * Build a PDF.js document from bytes.
     * dedicatedWorker=true → private worker (detection, destroyed with session);
     * false → reuse service-level shared PDFWorker.
     */
    private async openPdfDocument(sourceBytes: Uint8Array, dedicatedWorker: boolean) {
        const app = (window as any).PDFViewerApplication;
        const lib = (window as any).pdfjsLib ?? app?.pdfjsLib;
        if (!lib?.getDocument) return null;
        const data = new Uint8Array(sourceBytes);
        let worker: any = null;
        if (dedicatedWorker && lib.PDFWorker) worker = new lib.PDFWorker();
        else if (lib.PDFWorker) {
            if (!this.pdfWorker) this.pdfWorker = new lib.PDFWorker();
            worker = this.pdfWorker;
        }
        const opts: any = { data, verbosity: 0 };
        if (worker) opts.worker = worker;
        try {
            return { pdf: await lib.getDocument(opts).promise, dedicatedWorker: dedicatedWorker ? worker : null };
        } catch {
            try { if (dedicatedWorker) worker?.destroy?.(); } catch { /* */ }
            return null;
        }
    }

    /** Wait until viewer page views are not in renderingState===1 (or give up). */
    private async waitIdle(max = 60): Promise<void> {
        const v = (window as any).PDFViewerApplication?.pdfViewer;
        if (!v?.getPageView) return;
        const busy = () => {
            try {
                for (let i = 0, n = Math.max(1, Number(v.pagesCount ?? 1)); i < n; i++) {
                    if (v.getPageView(i)?.renderingState === 1) return true;
                }
            } catch { return false; }
            return false;
        };
        let f = 0;
        while (busy() && f++ < max) await new Promise<void>((r) => requestAnimationFrame(() => r()));
    }

    /** Render a PDF page rect into a PNG blob (transform shifts viewport to the box). */
    private async render(
        page: { getViewport: (o: { scale: number }) => unknown; render: (p: unknown) => { promise: Promise<void> } },
        rect: Rect, canvasW: number, canvasH: number, scale: number,
    ): Promise<Blob | null> {
        const canvas = new OffscreenCanvas(canvasW, canvasH) as any;
        const ctx = canvas.getContext('2d')! as OffscreenCanvasRenderingContext2D;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvasW, canvasH);
        await page.render({
            canvasContext: ctx as any, viewport: page.getViewport({ scale }),
            transform: [1, 0, 0, 1, -Math.min(rect.x1, rect.x2) * scale, -Math.min(rect.y1, rect.y2) * scale],
            intent: 'print',
        }).promise;
        return canvas.convertToBlob({ type: 'image/png' });
    }

    /** Push last crop as data-URL for optional debug UI. */
    private debug(blob: Blob): void {
        const r = new FileReader();
        r.onloadend = () => this.debugUrlSubject.next(r.result as string);
        r.readAsDataURL(blob);
    }
}
